// frontend/src/components/EmpresaPortal/EmpresaPortal.js
// Portal de acceso restringido para usuarios de empresa: solo pueden invitar
// candidatos dentro de los stacks habilitados consumiendo los créditos asignados por el admin.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload, Copy, BarChart3, History, AlertTriangle, X,
  Users, FileText, Layers, Clock, CheckCircle2, Mail, Settings,
  Building2, User, IdCard, Search, FilterX, ChevronLeft, ChevronRight,
  Moon, Sun, Link2, Check, Coins
} from 'lucide-react';
import './EmpresaPortal.css';
import EmpresaSidebar from './EmpresaSidebar';
import VistaProximamente from './VistaProximamente';
import CargaMasivaCandidatos from './CargaMasivaCandidatos';
import PruebasPersonalizadas from './PruebasPersonalizadas';
import StacksPersonalizados from './StacksPersonalizados';
import StacksSugeridos from './StacksSugeridos';
import UsuariosEmpresa from './UsuariosEmpresa';
import TourOnboarding from './TourOnboarding';
import NotificacionesBell from './NotificacionesBell';
import ConfiguracionMenu from './ConfiguracionMenu';
import DuplicadosCandidatos from '../Shared/DuplicadosCandidatos';

const POR_PAGINA = 8;

const VISTAS_PROXIMAMENTE = {
  candidatos: { icon: Users, titulo: 'Candidatos', descripcion: 'Pronto tendrás aquí una pantalla dedicada a gestionar a todos tus candidatos. Por ahora, encuéntralos en el Dashboard.' },
  evaluaciones: { icon: FileText, titulo: 'Evaluaciones', descripcion: 'Pronto podrás ver el detalle de todas las evaluaciones enviadas desde esta sección.' },
  stacks: { icon: Layers, titulo: 'Mis stacks', descripcion: 'Pronto tendrás aquí una pantalla dedicada a tus stacks personalizados. Por ahora, encuéntralos en el Dashboard.' },
  resultados: { icon: BarChart3, titulo: 'Resultados', descripcion: 'Pronto podrás comparar resultados de todos tus candidatos desde esta sección. Por ahora, encuéntralos en el Dashboard.' },
  configuracion: { icon: Settings, titulo: 'Configuración', descripcion: 'Más opciones de configuración estarán disponibles próximamente. Por ahora, revisa tus datos básicos desde el menú de configuración arriba.' }
};

const EmpresaPortal = ({ usuario, onLogout }) => {
  const navigate = useNavigate();
  const [tourVisto, setTourVisto] = useState(usuario?.tour_visto);
  const [info, setInfo] = useState(null);
  const [candidatos, setCandidatos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState(null);
  // Enlace copiado recientemente (token o 'feedback'), para mostrar "¡Copiado!"
  const [copiado, setCopiado] = useState(null);

  const enlaceDeToken = (token) => `${window.location.origin}/test?token=${token}`;

  const copiarEnlace = async (enlace, clave) => {
    try {
      await navigator.clipboard.writeText(enlace);
    } catch {
      // Navegadores sin portapapeles (o http): selección manual
      window.prompt('Copia el enlace de la prueba:', enlace);
      return;
    }
    setCopiado(clave);
    setTimeout(() => setCopiado(c => (c === clave ? null : c)), 2000);
  };

  // El enlace solo sirve mientras la prueba no esté terminada ni vencida
  const enlaceVigente = (c) => c.token && c.estado !== 'completada' &&
    (!c.fecha_limite || new Date(c.fecha_limite) >= new Date(new Date().toDateString()));
  const [form, setForm] = useState({ nombre_completo: '', email: '', curp: '', id_stack: '', id_prueba: '', enviar_email: true });
  const [modoEnvio, setModoEnvio] = useState('stack'); // 'stack' = stack sugerido completo, 'prueba' = una sola prueba
  const [catalogoPruebas, setCatalogoPruebas] = useState([]);
  const [mostrarCargaMasiva, setMostrarCargaMasiva] = useState(false);
  const [mostrarDuplicados, setMostrarDuplicados] = useState(false);
  const [historial, setHistorial] = useState({ abierto: false, candidato: null, asignaciones: [] });
  const [compararStackId, setCompararStackId] = useState('');
  const [comparacion, setComparacion] = useState(null);
  const [compararLoading, setCompararLoading] = useState(false);
  const [compararError, setCompararError] = useState(null);

  const [vista, setVista] = useState('dashboard');
  const [sidebarColapsado, setSidebarColapsado] = useState(false);
  const [modoOscuro, setModoOscuro] = useState(() => {
    try { return localStorage.getItem('psytest_tema') === 'oscuro'; } catch { return false; }
  });
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroStack, setFiltroStack] = useState('');
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    cargarDatos();
  }, []);

  useEffect(() => {
    try { localStorage.setItem('psytest_tema', modoOscuro ? 'oscuro' : 'claro'); } catch { /* localStorage no disponible: el tema simplemente no se recuerda */ }
  }, [modoOscuro]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      setError(null);

      const [infoRes, candidatosRes, catalogoRes] = await Promise.all([
        fetch('/api/empresas/me/info', { credentials: 'include' }),
        fetch('/api/candidatos/mios', { credentials: 'include' }),
        fetch('/api/empresas/me/catalogo-pruebas', { credentials: 'include' })
      ]);

      if (!infoRes.ok) throw new Error('No se pudo cargar la información de tu empresa');
      if (!candidatosRes.ok) throw new Error('No se pudo cargar la lista de candidatos');

      const infoData = await infoRes.json();
      const candidatosData = await candidatosRes.json();
      const catalogoData = catalogoRes.ok ? await catalogoRes.json() : [];

      setInfo(infoData);
      setCandidatos(Array.isArray(candidatosData) ? candidatosData : []);
      setCatalogoPruebas(Array.isArray(catalogoData) ? catalogoData : []);

      if (!form.id_stack && infoData.stacks?.length === 1) {
        setForm(prev => ({ ...prev, id_stack: infoData.stacks[0].id }));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const getIniciales = (nombre) => {
    if (!nombre) return '?';
    const partes = nombre.trim().split(/\s+/);
    const iniciales = partes.slice(0, 2).map(p => p[0]?.toUpperCase() || '').join('');
    return iniciales || '?';
  };

  // Créditos (ver backend/src/services/creditosService.js): el costo se
  // reserva al invitar y se cobra cuando el candidato inicia
  const creditos = info?.creditos;
  const creditosIlimitados = Boolean(creditos?.ilimitado);
  const creditosLibres = creditosIlimitados ? Infinity : (creditos?.libres ?? 0);
  const costoIndividual = info?.costo_prueba_individual ?? 1;
  const stackSeleccionado = info?.stacks?.find(s => String(s.id) === String(form.id_stack));
  const costoSeleccion = modoEnvio === 'stack' ? (stackSeleccionado?.costo_creditos ?? null) : (form.id_prueba ? costoIndividual : null);
  // Sin créditos ni para la invitación más barata: se oculta el formulario
  const cupoAgotado = Boolean(info) && !creditosIlimitados && creditosLibres < costoIndividual;
  const seleccionExcedeSaldo = costoSeleccion !== null && costoSeleccion > creditosLibres;
  const textoCreditos = (n) => `${n} crédito${n === 1 ? '' : 's'}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFeedback(null);

    const seleccionValida = modoEnvio === 'stack' ? !!form.id_stack : !!form.id_prueba;
    if (seleccionValida && seleccionExcedeSaldo) {
      setFeedback({
        type: 'error',
        message: `Esta invitación requiere ${textoCreditos(costoSeleccion)} y tienes ${textoCreditos(creditosLibres)} libre${creditosLibres === 1 ? '' : 's'}.`
      });
      return;
    }
    if (!form.nombre_completo.trim() || !form.email.trim() || !form.curp.trim() || !seleccionValida) {
      setFeedback({
        type: 'error',
        message: `Completa todos los campos, incluyendo ${modoEnvio === 'stack' ? 'el stack a enviar' : 'la prueba a enviar'}.`
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const candidatoRes = await fetch('/api/candidatos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          nombre_completo: form.nombre_completo,
          email: form.email,
          curp: form.curp.toUpperCase()
        })
      });
      const candidatoData = await candidatoRes.json();
      if (!candidatoRes.ok) {
        throw new Error(candidatoData.error || 'No se pudo registrar al candidato');
      }

      const asignacionRes = await fetch('/api/asignaciones', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          id_candidato: candidatoData.id,
          ...(modoEnvio === 'stack' ? { id_stack: form.id_stack } : { id_prueba: form.id_prueba }),
          enviar_email: form.enviar_email
        })
      });
      const asignacionData = await asignacionRes.json();
      if (!asignacionRes.ok) {
        throw new Error(asignacionData.error || 'No se pudo generar la invitación');
      }

      let mensaje;
      if (asignacionData.email_enviado) {
        mensaje = `Invitación enviada a ${form.email}. También puedes compartir el enlace directamente:`;
      } else if (asignacionData.email_omitido) {
        mensaje = 'Candidato registrado sin enviar correo. Comparte este enlace con el candidato:';
      } else {
        mensaje = 'Candidato registrado, pero hubo un problema al enviar el correo. Comparte este enlace con el candidato:';
      }

      setFeedback({ type: 'success', message: mensaje, enlace: asignacionData.enlace });
      setForm({
        nombre_completo: '', email: '', curp: '',
        id_stack: info.stacks.length === 1 ? info.stacks[0].id : '',
        id_prueba: '',
        enviar_email: true
      });
      await cargarDatos();
    } catch (err) {
      setFeedback({ type: 'error', message: err.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const verResultados = (candidatoId) => {
    navigate(`/resultados/${candidatoId}`);
  };

  const abrirHistorial = async (candidato) => {
    try {
      const response = await fetch(`/api/candidatos/${candidato.id}/detalle`, { credentials: 'include' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'No se pudo cargar el historial');
      setHistorial({ abierto: true, candidato, asignaciones: data.asignaciones || [] });
    } catch (err) {
      setFeedback({ type: 'error', message: err.message });
    }
  };

  const cerrarHistorial = () => {
    setHistorial({ abierto: false, candidato: null, asignaciones: [] });
  };

  const verResultadosDeAsignacion = (asignacionId) => {
    const candidatoId = historial.candidato.id;
    cerrarHistorial();
    navigate(`/resultados/${candidatoId}?asignacion_id=${asignacionId}`);
  };

  const verResultadosDesdeComparacion = (candidatoId, asignacionId) => {
    navigate(`/resultados/${candidatoId}?asignacion_id=${asignacionId}`);
  };

  const cargarComparacion = async (stackId) => {
    setCompararStackId(stackId);
    setComparacion(null);
    setCompararError(null);
    if (!stackId) return;

    setCompararLoading(true);
    try {
      const response = await fetch(`/api/resultados/comparar?stack_id=${stackId}`, { credentials: 'include' });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'No se pudo cargar la comparación');
      setComparacion(data);
    } catch (err) {
      setCompararError(err.message);
    } finally {
      setCompararLoading(false);
    }
  };

  // Columnas de personalidad a mostrar: la unión de las pruebas de
  // personalidad presentes en los resultados (normalmente iguales para
  // todos, ya que las define el stack).
  const columnasPersonalidad = comparacion?.candidatos.length
    ? Array.from(
        new Map(
          comparacion.candidatos
            .flatMap(c => c.pruebas_personalidad)
            .map(p => [p.clave, p.nombre])
        )
      )
    : [];

  // ----- Derivados para las tarjetas de resumen del Dashboard -----
  const totalCandidatos = candidatos.length;
  const completadas = candidatos.filter(c => c.estado === 'completada').length;
  const pendientes = totalCandidatos - completadas;
  const proximoVencimiento = creditos?.proximo_vencimiento;

  // ----- Búsqueda, filtros y paginación de "Candidatos invitados" -----
  const estadosDisponibles = Array.from(new Set(candidatos.map(c => c.estado || 'pendiente')));
  const stacksDisponibles = Array.from(new Set(candidatos.map(c => c.stack_nombre).filter(Boolean)));

  const candidatosFiltrados = candidatos.filter(c => {
    const texto = busqueda.trim().toLowerCase();
    const coincideBusqueda = !texto
      || c.nombre_completo?.toLowerCase().includes(texto)
      || c.email?.toLowerCase().includes(texto);
    const coincideEstado = !filtroEstado || (c.estado || 'pendiente') === filtroEstado;
    const coincideStack = !filtroStack || c.stack_nombre === filtroStack;
    return coincideBusqueda && coincideEstado && coincideStack;
  });

  const totalPaginas = Math.max(Math.ceil(candidatosFiltrados.length / POR_PAGINA), 1);
  const paginaSegura = Math.min(pagina, totalPaginas);
  const candidatosPagina = candidatosFiltrados.slice((paginaSegura - 1) * POR_PAGINA, paginaSegura * POR_PAGINA);
  const hayFiltrosActivos = !!(busqueda || filtroEstado || filtroStack);

  const limpiarFiltros = () => {
    setBusqueda('');
    setFiltroEstado('');
    setFiltroStack('');
    setPagina(1);
  };

  const fechaHoy = new Date().toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const fechaHoyCap = fechaHoy.charAt(0).toUpperCase() + fechaHoy.slice(1);

  if (loading) {
    return <div className="ep-loading">Cargando tu portal...</div>;
  }

  if (error) {
    return (
      <div className="ep-loading">
        <p>{error}</p>
        <button className="ep-btn-secondary" onClick={onLogout}>Cerrar sesión</button>
      </div>
    );
  }

  return (
    <div className="ep-container" data-theme={modoOscuro ? 'dark' : 'light'}>
      <div className="ep-topbar">
        <div className="ep-brand">
          <img src="/isotipotransparente.png" alt="PsyTest" className="ep-brand-logo" />
          <div className="ep-brand-text">
            <span className="ep-brand-title">PsyTest</span>
            <span className="ep-sub">{info?.nombre} · Portal de administración de candidatos</span>
          </div>
        </div>
        <div className="ep-topbar-actions">
          <button
            type="button"
            className="ep-theme-toggle"
            onClick={() => setModoOscuro(v => !v)}
            title={modoOscuro ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
          >
            {modoOscuro ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <NotificacionesBell />
          <ConfiguracionMenu usuario={usuario} empresaNombre={info?.nombre} />
          <button className="ep-btn-secondary" onClick={onLogout}>Cerrar sesión</button>
        </div>
      </div>

      {usuario && (
        <TourOnboarding
          usuario={{ ...usuario, tour_visto: tourVisto }}
          onTourVisto={() => setTourVisto(true)}
        />
      )}

      <div className="ep-body">
        <EmpresaSidebar
          vista={vista}
          onNavegar={setVista}
          colapsado={sidebarColapsado}
          onToggle={() => setSidebarColapsado(v => !v)}
        />

        <main className="ep-main">
          {vista === 'usuarios' ? (
            <div className="ep-shell">
              <UsuariosEmpresa />
            </div>
          ) : vista !== 'dashboard' ? (
            <VistaProximamente {...VISTAS_PROXIMAMENTE[vista]} />
          ) : (
            <div className="ep-shell">
              <div className="ep-welcome">
                <div>
                  <span className="ep-welcome-eyebrow">Bienvenido/a</span>
                  <h1>{usuario?.nombre || 'Administrador'} 👋</h1>
                  <p>Gestiona tus candidatos, evaluaciones y stacks desde un solo lugar.</p>
                </div>
                <div className="ep-welcome-meta">
                  <span>{fechaHoyCap}</span>
                  <span className="ep-welcome-empresa"><Building2 size={14} /> {info?.nombre}</span>
                </div>
              </div>

              <div className="ep-stats-grid">
                <div className="ep-stat-card" data-tour="cupo">
                  <div className="ep-stat-icon ep-stat-icon-teal"><Coins size={20} /></div>
                  <div className="ep-stat-body">
                    <span className="ep-stat-label">Créditos disponibles</span>
                    <span className="ep-stat-value">{creditosIlimitados ? 'Ilimitados' : (creditos?.libres ?? 0)}</span>
                    <span className="ep-stat-caption">
                      {creditosIlimitados
                        ? 'sin límite de evaluaciones'
                        : proximoVencimiento
                          ? `${textoCreditos(proximoVencimiento.cantidad)} vence${proximoVencimiento.cantidad === 1 ? '' : 'n'} el ${new Date(proximoVencimiento.fecha).toLocaleDateString('es-MX')}`
                          : 'listos para invitar'}
                    </span>
                  </div>
                </div>

                <div className="ep-stat-card">
                  <div className="ep-stat-icon ep-stat-icon-green"><CheckCircle2 size={20} /></div>
                  <div className="ep-stat-body">
                    <span className="ep-stat-label">Evaluaciones completadas</span>
                    <span className="ep-stat-value">{completadas}</span>
                    <span className="ep-stat-caption">de {totalCandidatos} candidato{totalCandidatos === 1 ? '' : 's'}</span>
                  </div>
                </div>

                <div className="ep-stat-card">
                  <div className="ep-stat-icon ep-stat-icon-amber"><Clock size={20} /></div>
                  <div className="ep-stat-body">
                    <span className="ep-stat-label">Pendientes</span>
                    <span className="ep-stat-value">{pendientes}</span>
                    <span className="ep-stat-caption">candidatos</span>
                  </div>
                </div>

                <div className="ep-stat-card">
                  <div className="ep-stat-icon ep-stat-icon-purple"><Mail size={20} /></div>
                  <div className="ep-stat-body">
                    <span className="ep-stat-label">Créditos reservados</span>
                    <span className="ep-stat-value">{creditos?.reservados ?? 0}</span>
                    <span className="ep-stat-caption">
                      en {creditos?.invitaciones_pendientes ?? 0} invitaci{(creditos?.invitaciones_pendientes ?? 0) === 1 ? 'ón' : 'ones'} sin iniciar · {totalCandidatos} candidato{totalCandidatos === 1 ? '' : 's'} en total
                    </span>
                  </div>
                </div>
              </div>

              <div className="ep-card">
                <div className="ep-card-header">
                  <h2>Invitar candidato</h2>
                  {info?.stacks?.length > 0 && !cupoAgotado && (
                    <button type="button" className="ep-btn-secondary" onClick={() => setMostrarCargaMasiva(true)}>
                      <Upload size={15} /> Carga masiva
                    </button>
                  )}
                </div>
                {info?.stacks?.length === 0 && (
                  <p className="ep-empty">Tu empresa aún no tiene stacks de pruebas habilitados. Contacta al administrador.</p>
                )}
                {cupoAgotado && (
                  <p className="ep-warning">
                    No tienes créditos libres para invitar candidatos
                    {creditos?.reservados > 0 ? ` (${textoCreditos(creditos.reservados)} están reservados para invitaciones que aún no se inician)` : ''}.
                    Para adquirir más, escríbenos a <a href="mailto:contacto@doublecoding.com">contacto@doublecoding.com</a>.
                  </p>
                )}
                {info?.stacks?.length > 0 && !cupoAgotado && (
                  <form onSubmit={handleSubmit} className="ep-form" data-tour="form-candidato">
                    <div className="ep-field-row">
                      <div className="ep-field">
                        <label>Nombre completo</label>
                        <div className="ep-input-icon-wrap">
                          <User size={15} />
                          <input type="text" name="nombre_completo" value={form.nombre_completo} onChange={handleChange} placeholder="Ej. María González Pérez" required disabled={isSubmitting} />
                        </div>
                      </div>
                      <div className="ep-field">
                        <label>CURP</label>
                        <div className="ep-input-icon-wrap">
                          <IdCard size={15} />
                          <input type="text" name="curp" value={form.curp} onChange={handleChange} placeholder="Ej. GOGM950101MDFNDR07" required maxLength="18" disabled={isSubmitting} />
                        </div>
                      </div>
                    </div>

                    <div className="ep-field-row">
                      <div className="ep-field">
                        <label>Email</label>
                        <div className="ep-input-icon-wrap">
                          <Mail size={15} />
                          <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="ejemplo@correo.com" required disabled={isSubmitting} />
                        </div>
                      </div>
                      <div className="ep-field" data-tour="stack-select">
                        <label>{modoEnvio === 'stack' ? 'Stacks sugeridos' : 'Prueba individual'}</label>
                        <div className="ep-input-icon-wrap">
                          <Layers size={15} />
                          {modoEnvio === 'stack' ? (
                            <select name="id_stack" value={form.id_stack} onChange={handleChange} disabled={isSubmitting}>
                              <option value="">Selecciona un stack</option>
                              {info.stacks.map(stack => (
                                <option key={stack.id} value={stack.id}>{stack.nombre} · {textoCreditos(stack.costo_creditos)}</option>
                              ))}
                            </select>
                          ) : (
                            <select name="id_prueba" value={form.id_prueba} onChange={handleChange} disabled={isSubmitting}>
                              <option value="">Selecciona una prueba</option>
                              {catalogoPruebas.map(prueba => (
                                <option key={prueba.id} value={prueba.id}>{prueba.nombre} · {textoCreditos(costoIndividual)}</option>
                              ))}
                            </select>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="ep-field">
                      <label>¿Qué quieres enviar?</label>
                      <div className="ep-send-options">
                        <label className={`ep-send-option ${modoEnvio === 'stack' ? 'active' : ''}`}>
                          <input type="radio" name="modo_envio" checked={modoEnvio === 'stack'} onChange={() => setModoEnvio('stack')} disabled={isSubmitting} />
                          <span className="ep-send-option-icon ep-send-option-icon-teal"><Layers size={18} /></span>
                          <span className="ep-send-option-text">
                            <strong>Stack sugerido</strong>
                            <small>Envía el stack completo recomendado para el puesto.</small>
                          </span>
                        </label>
                        <label className={`ep-send-option ${modoEnvio === 'prueba' ? 'active' : ''}`}>
                          <input type="radio" name="modo_envio" checked={modoEnvio === 'prueba'} onChange={() => setModoEnvio('prueba')} disabled={isSubmitting} />
                          <span className="ep-send-option-icon ep-send-option-icon-blue"><FileText size={18} /></span>
                          <span className="ep-send-option-text">
                            <strong>Prueba individual</strong>
                            <small>Selecciona una sola prueba para enviar.</small>
                          </span>
                        </label>
                      </div>
                    </div>

                    <div className="ep-field ep-field-checkbox">
                      <label className="ep-checkbox-label">
                        <input
                          type="checkbox"
                          name="enviar_email"
                          checked={form.enviar_email}
                          onChange={handleChange}
                          disabled={isSubmitting}
                        />
                        Enviar correo de invitación al candidato
                      </label>
                      {!form.enviar_email && (
                        <p className="ep-hint">No se enviará correo. Podrás copiar el enlace de la prueba manualmente al terminar.</p>
                      )}
                    </div>
                    {costoSeleccion !== null && (
                      <p className={`ep-costo-invitacion ${seleccionExcedeSaldo ? 'insuficiente' : ''}`}>
                        {seleccionExcedeSaldo
                          ? `Esta invitación requiere ${textoCreditos(costoSeleccion)} y tienes ${textoCreditos(creditosLibres)} libre${creditosLibres === 1 ? '' : 's'}.`
                          : `Esta invitación reserva ${textoCreditos(costoSeleccion)}. Se cobran cuando el candidato inicia la evaluación; si no la inicia antes de que venza el enlace, se liberan.`}
                      </p>
                    )}
                    <button type="submit" className="ep-btn-primary" disabled={isSubmitting || seleccionExcedeSaldo} data-tour="btn-invitar">
                      {isSubmitting ? 'Enviando...' : '+ Invitar candidato'}
                    </button>
                  </form>
                )}
                {feedback && (
                  <div className={`ep-feedback ep-feedback-${feedback.type}`}>
                    {feedback.message}
                    {feedback.enlace && (
                      <div className="ep-feedback-enlace">
                        <code>{feedback.enlace}</code>
                        <button type="button" className="ep-btn-link" onClick={() => copiarEnlace(feedback.enlace, 'feedback')}>
                          {copiado === 'feedback' ? <><Check size={13} /> ¡Copiado!</> : <><Copy size={13} /> Copiar enlace</>}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="ep-card">
                <div className="ep-card-header">
                  <h2>Candidatos invitados</h2>
                  {candidatos.length > 0 && (
                    <button type="button" className="ep-btn-secondary" onClick={() => setMostrarDuplicados(true)}>
                      <Copy size={15} /> Duplicados
                    </button>
                  )}
                </div>

                {candidatos.length === 0 ? (
                  <p className="ep-empty">Aún no has invitado a ningún candidato.</p>
                ) : (
                  <>
                    <div className="ep-table-toolbar">
                      <div className="ep-input-icon-wrap ep-search-wrap">
                        <Search size={15} />
                        <input
                          type="text"
                          placeholder="Buscar por nombre o email..."
                          value={busqueda}
                          onChange={(e) => { setBusqueda(e.target.value); setPagina(1); }}
                        />
                      </div>
                      <select value={filtroEstado} onChange={(e) => { setFiltroEstado(e.target.value); setPagina(1); }}>
                        <option value="">Todos los estados</option>
                        {estadosDisponibles.map(estado => (
                          <option key={estado} value={estado}>{estado}</option>
                        ))}
                      </select>
                      <select value={filtroStack} onChange={(e) => { setFiltroStack(e.target.value); setPagina(1); }}>
                        <option value="">Todos los stacks</option>
                        {stacksDisponibles.map(stack => (
                          <option key={stack} value={stack}>{stack}</option>
                        ))}
                      </select>
                      {hayFiltrosActivos && (
                        <button type="button" className="ep-btn-secondary" onClick={limpiarFiltros}>
                          <FilterX size={14} /> Limpiar
                        </button>
                      )}
                    </div>

                    {candidatosFiltrados.length === 0 ? (
                      <p className="ep-empty">No se encontraron candidatos con esos filtros.</p>
                    ) : (
                      <div className="ep-table-wrap">
                        <table className="ep-table">
                          <thead>
                            <tr>
                              <th></th>
                              <th>Nombre</th>
                              <th>Email</th>
                              <th>Stack</th>
                              <th>Estado</th>
                              <th>Fecha</th>
                              <th>Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {candidatosPagina.map(c => (
                              <tr key={c.id}>
                                <td>
                                  <span className="ep-candidato-avatar">
                                    {c.foto_url ? (
                                      <img src={c.foto_url} alt="" className="ep-candidato-avatar-foto" />
                                    ) : (
                                      getIniciales(c.nombre_completo)
                                    )}
                                  </span>
                                </td>
                                <td>{c.nombre_completo}</td>
                                <td>{c.email}</td>
                                <td>{c.stack_nombre || '—'}</td>
                                <td>
                                  <span className={`ep-status ep-status-${c.estado || 'pendiente'}`}>
                                    {c.estado || 'pendiente'}
                                  </span>
                                  {c.riesgo_fraude && (
                                    <span
                                      className={`ep-badge-riesgo ep-badge-riesgo-${c.riesgo_fraude}`}
                                      title={`${c.riesgo_fraude === 'rojo' ? 'Muy probable' : 'Probable'} indicio de fraude en sus respuestas: ${c.cambios_pestana} cambio(s) de pestaña y ${c.respuestas_rapidas} respuesta(s) sospechosamente rápida(s) durante pruebas de habilidad.`}
                                    >
                                      <AlertTriangle size={12} /> Riesgo
                                    </span>
                                  )}
                                </td>
                                <td>{c.fecha_asignacion ? new Date(c.fecha_asignacion).toLocaleDateString('es-MX') : '—'}</td>
                                <td>
                                  <div className="ep-row-actions">
                                    {enlaceVigente(c) && (
                                      <button
                                        type="button"
                                        className="ep-btn-link"
                                        onClick={() => copiarEnlace(enlaceDeToken(c.token), c.token)}
                                        title="Copiar el enlace de la prueba para enviarlo al candidato"
                                      >
                                        {copiado === c.token ? <><Check size={13} /> ¡Copiado!</> : <><Link2 size={13} /> Copiar enlace</>}
                                      </button>
                                    )}
                                    {c.estado === 'completada' && (
                                      <button type="button" className="ep-btn-link" onClick={() => verResultados(c.id)}>
                                        <BarChart3 size={13} /> Ver resultados
                                      </button>
                                    )}
                                    {c.total_asignaciones > 1 && (
                                      <button type="button" className="ep-btn-link" onClick={() => abrirHistorial(c)}>
                                        <History size={13} /> {c.total_asignaciones} intentos
                                      </button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {candidatosFiltrados.length > 0 && (
                      <div className="ep-pagination">
                        <span>
                          Mostrando {(paginaSegura - 1) * POR_PAGINA + 1} a {Math.min(paginaSegura * POR_PAGINA, candidatosFiltrados.length)} de {candidatosFiltrados.length} candidato{candidatosFiltrados.length === 1 ? '' : 's'}
                        </span>
                        <div className="ep-pagination-controls">
                          <button type="button" disabled={paginaSegura === 1} onClick={() => setPagina(p => p - 1)}>
                            <ChevronLeft size={15} />
                          </button>
                          <span className="ep-pagination-page">{paginaSegura}</span>
                          <button type="button" disabled={paginaSegura === totalPaginas} onClick={() => setPagina(p => p + 1)}>
                            <ChevronRight size={15} />
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="ep-card">
                <h2>Comparar candidatos</h2>

                <div className="ep-compare-intro">
                  <div className="ep-empty-bubble ep-empty-bubble-sm">
                    <BarChart3 size={22} />
                  </div>
                  <p>
                    Elige un stack para ver, lado a lado, a todos los candidatos que ya lo completaron y
                    comparar sus habilidades y resultados. Se ordenan por puntaje en las pruebas de
                    habilidad (Terman/OTIS); las pruebas de personalidad se muestran como referencia, no
                    se usan para ordenar.
                  </p>
                </div>

                <div className="ep-field ep-field-inline">
                  <label>Stack a comparar</label>
                  <select value={compararStackId} onChange={(e) => cargarComparacion(e.target.value)}>
                    <option value="">Selecciona un stack</option>
                    {(info?.stacks || []).map(stack => (
                      <option key={stack.id} value={stack.id}>{stack.nombre}</option>
                    ))}
                  </select>
                </div>

                {compararLoading && <p className="ep-empty">Cargando comparación...</p>}
                {compararError && <div className="ep-feedback ep-feedback-error">{compararError}</div>}

                {comparacion && (
                  comparacion.candidatos.length === 0 ? (
                    <p className="ep-empty">Todavía no hay candidatos que hayan completado este stack.</p>
                  ) : (
                    <div className="ep-table-wrap">
                      <table className="ep-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Candidato</th>
                            <th>Puntaje de habilidad</th>
                            {columnasPersonalidad.map(([clave, nombre]) => (
                              <th key={clave}>{nombre}</th>
                            ))}
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {comparacion.candidatos.map((c, idx) => (
                            <tr key={c.candidato_id}>
                              <td>{idx + 1}</td>
                              <td>{c.nombre_completo}</td>
                              <td>
                                {c.porcentaje_habilidad === null ? (
                                  <span className="ep-empty">— (sin prueba de habilidad)</span>
                                ) : (
                                  <strong>{c.porcentaje_habilidad}%</strong>
                                )}
                              </td>
                              {columnasPersonalidad.map(([clave]) => {
                                const prueba = c.pruebas_personalidad.find(p => p.clave === clave);
                                return <td key={clave}>{prueba?.resumen || '—'}</td>;
                              })}
                              <td>
                                <button type="button" className="ep-btn-link" onClick={() => verResultadosDesdeComparacion(c.candidato_id, c.asignacion_id)}>
                                  <BarChart3 size={13} /> Ver reporte
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                )}
              </div>

              <StacksSugeridos />

              <div className="ep-grid-2col">
                <PruebasPersonalizadas onCambio={cargarDatos} />
                <StacksPersonalizados onCambio={cargarDatos} />
              </div>

              <div className="ep-footer">
                <div className="ep-footer-brand">
                  <img src="/isotipotransparente.png" alt="PsyTest" className="ep-footer-logo" />
                  <span>PsyTest · {info?.nombre} · Portal de administración de candidatos</span>
                </div>
                <span className="ep-footer-help">¿Necesitas ayuda? Usa el botón "Ver tour" para repasar el recorrido guiado.</span>
              </div>
            </div>
          )}
        </main>
      </div>

      {mostrarCargaMasiva && (
        <CargaMasivaCandidatos
          info={info}
          onClose={() => setMostrarCargaMasiva(false)}
          onCompletado={cargarDatos}
        />
      )}

      {mostrarDuplicados && (
        <DuplicadosCandidatos
          onClose={() => setMostrarDuplicados(false)}
          onCombinado={cargarDatos}
        />
      )}

      {historial.abierto && (
        <div className="ep-modal-overlay">
          <div className="ep-modal">
            <div className="ep-modal-header">
              <h2>Historial de {historial.candidato?.nombre_completo}</h2>
              <button className="ep-modal-close" onClick={cerrarHistorial}><X size={18} /></button>
            </div>
            <div className="ep-modal-body">
              {historial.asignaciones.length === 0 ? (
                <p className="ep-empty">Este candidato no tiene asignaciones.</p>
              ) : (
                <div className="ep-table-wrap">
                  <table className="ep-table">
                    <thead>
                      <tr>
                        <th>Stack</th>
                        <th>Asignado</th>
                        <th>Estado</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {historial.asignaciones.map(asig => (
                        <tr key={asig.asignacion_id}>
                          <td>{asig.stack_nombre || '—'}</td>
                          <td>{new Date(asig.fecha_asignacion).toLocaleDateString('es-MX')}</td>
                          <td>
                            <span className={`ep-status ep-status-${asig.estado || 'pendiente'}`}>
                              {asig.estado || 'pendiente'}
                            </span>
                          </td>
                          <td>
                            {asig.estado === 'completada' && (
                              <button type="button" className="ep-btn-link" onClick={() => verResultadosDeAsignacion(asig.asignacion_id)}>
                                <BarChart3 size={13} /> Ver resultados
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="ep-modal-footer">
              <button className="ep-btn-secondary" onClick={cerrarHistorial}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmpresaPortal;
